import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePasswordValidation, useResetPasswordForm } from '@open-tender/utils'
import {
  selectResetPassword,
  resetPassword,
  resetPasswordReset,
  useAppDispatch,
  useAppSelector,
  openModal,
} from '@open-tender/cloud'
import {
  ButtonLink,
  ButtonSubmit,
  FormError,
  FormInputs,
  FormSubmit,
  FormWrapper,
} from 'components'
import PasswordRequirements from 'components/PasswordRequirements'
import { FormFieldType } from '@open-tender/types'

const ResetPasswordForm = ({ resetToken }: { resetToken: string }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { success, loading, error } = useAppSelector(selectResetPassword)

  const {
    handlePasswordChange,
    isPasswordValid,
    shouldShowRequirements,
    getPasswordErrors,
  } = usePasswordValidation()

  const reset = useCallback(
    (new_password: string, resetToken: string) =>
      dispatch(resetPassword({ new_password, resetToken })),
    [dispatch]
  )

  const {
    submitRef,
    fields,
    data,
    errors,
    submitting,
    handleChange,
    handleSubmit,
  } = useResetPasswordForm(loading, error, reset, resetToken)

  const handleLogin = () => {
    const args = {
      type: 'login',
      args: { callback: () => navigate('/account') },
    }
    dispatch(openModal(args))
  }

  const onChange = (field: FormFieldType, value: string | number | boolean) => {
    handleChange(field.name, value)

    if (field.name === 'new_password') {
      handlePasswordChange()
    }
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (data.new_password) {
      handlePasswordChange()
    }

    // Prevent submission if password validation fails
    if (!data.new_password || !isPasswordValid(data.new_password as string)) {
      return
    }

    handleSubmit(e)
  }

  useEffect(() => {
    dispatch(resetPasswordReset())
    return () => {
      dispatch(resetPasswordReset())
    }
  }, [dispatch])

  useEffect(() => {
    if (error) window.scrollTo(0, 0)
  }, [error])

  // Separate password field from other fields for custom rendering
  const passwordField = fields.find((f) => f.name === 'new_password')
  const otherFields = fields.filter((f) => f.name !== 'new_password')
  const showPasswordRequirements = shouldShowRequirements(
    data.new_password as string
  )
  const passwordErrors = getPasswordErrors(
    data.new_password as string,
    errors,
    'new_password'
  )

  return (
    <FormWrapper>
      {success ? (
        <>
          <p style={{ margin: '0 0 2rem' }}>
            Success! Your password has been reset.
          </p>
          <ButtonLink onClick={handleLogin}>
            Click here to log into your account
          </ButtonLink>
        </>
      ) : (
        <form id="reset-password-form" onSubmit={handleFormSubmit} noValidate>
          <FormError
            errMsg={errors.token || errors.form}
            style={{ margin: '0 0 2rem' }}
          />
          {passwordField && (
            <div>
              <FormInputs
                fields={[passwordField]}
                data={data}
                onChange={(field, value) => {
                  onChange(field, value)
                }}
                errors={passwordErrors} // Show backend errors, PasswordRequirements shows frontend validation
              />
              {showPasswordRequirements && (
                <PasswordRequirements
                  password={(data.new_password as string) || ''}
                />
              )}
            </div>
          )}
          <FormInputs
            fields={otherFields}
            data={data}
            onChange={onChange}
            errors={errors}
          />
          <FormSubmit>
            <ButtonSubmit submitRef={submitRef} submitting={submitting}>
              {submitting ? 'Submitting...' : 'Submit'}
            </ButtonSubmit>
          </FormSubmit>
        </form>
      )}
    </FormWrapper>
  )
}

export default ResetPasswordForm
